import React, { useCallback } from 'react';
import PropTypes, { any } from 'prop-types';

import { comparator, ComparatorTypes, getGradClassValue } from '../../../../../utils/comparator';
import { getRatingDetails, setRatingChoice, getPlayerName } from '../../../../../utils';
import { CLUB_PROFILE_VIEW_TYPES, getClassYearIdFromStatus, CLASS_YEAR_IDS } from '../../../../../utils/constants';
import { SortType } from '../../../../ColumnHeader';
import { PlayerCard } from '../../../../cards';
import RosterHeader from './RosterHeader';

const Actions = {
    UPDATE: 'update',
    REMOVE: 'remove',
    SET_REDSHIRT: 'set_redshirt',
    REMOVE_REDSHIRT: 'remove_redshirt',
};
const SortParams = {
    NAME: 'name',
    SINGLES_RATING: 'singlesRating',
    DOUBLES_RATING: 'doublesRating',
    PLAYER_COLLEGE_DETAILS: 'playerCollegeDetails',
    PLAYER_HIGH_SCHOOL_DETAILS: 'playerHighSchoolDetails',
    LAST_NAME: 'lastName',
    FIRST_NAME: 'firstName',
};
const SortValues = {
    PRIMARY_SORT_VALUE: 'primarySortValue',
    SECONDARY_SORT_VALUE: 'secondarySortValue',
};

const Roster = ({
    rosterTabState,
    isAdminOrOwner,
    clubDetails,
    clubType,
    featureGates,
    user,
    schoolDetails,
    updateRosterTabState,
    showTennisRatings = true,
    showPickleBallRatings = false,
    userIsPowered,
    checkGate,
}) => {
    const isRostersRolloutEnabled = checkGate && featureGates ? checkGate(featureGates.ROSTERS_ROLLOUT) : false;
    const { isCollege } = clubDetails.tierType;
    const isRosterLocked = isRostersRolloutEnabled && isCollege && schoolDetails?.locked;

    const onMenuSelected = useCallback((e, playerId, playerName, selected) => {
        // If roster is locked, show locked popup instead of allowing any action
        if (isRosterLocked) {
            updateRosterTabState({
                ...rosterTabState,
                showRosterLockedPopUp: true,
            });
            return;
        }

        switch (selected.value) {
            case 'update':
                updateRosterTabState({
                    ...rosterTabState,
                    showUpdatePlayerPopUp: true,
                    updatePlayerOnRosterPlayerId: playerId,
                    updatePlayerOnRosterPlayerName: playerName,
                });
                break;
            case 'remove':
                updateRosterTabState({
                    ...rosterTabState,
                    showRemovePlayerConfirmationPopUp: true,
                    removePlayerFromRosterPlayerId: playerId,
                    removePlayerFromRosterPlayerName: playerName,
                });
                break;
            case 'set_redshirt':
                updateRosterTabState({
                    ...rosterTabState,
                    showRedshirtConfirmationPopUp: true,
                    redshirtPlayerId: playerId,
                    redshirtPlayerName: playerName,
                });
                break;
            case 'remove_redshirt':
                updateRosterTabState({
                    ...rosterTabState,
                    showRemoveRedshirtConfirmationPopUp: true,
                    removeRedshirtPlayerId: playerId,
                    removeRedshirtPlayerName: playerName,
                });
                break;
            default:
                break;
        }
    }, [rosterTabState, updateRosterTabState, isRosterLocked]);

    const getRosterComparatorType = (sortParam) => {
        switch (sortParam) {
            case SortParams.SINGLES_RATING:
            case SortParams.DOUBLES_RATING:
            case SortParams.PLAYER_COLLEGE_DETAILS:
            case SortParams.PLAYER_HIGH_SCHOOL_DETAILS:
                return ComparatorTypes.NUMBER;
            default:
                return ComparatorTypes.STRING;
        }
    };

    const getComparatorArguments = (sortParam, sortType) => {
        const primarySortArg = {
            type: getRosterComparatorType(sortParam),
            field: SortValues.PRIMARY_SORT_VALUE,
            descend: sortType === SortType.DESC,
        };
        const args = [primarySortArg];

        if ([SortParams.NAME, SortParams.SINGLES_RATING, SortParams.DOUBLES_RATING].includes(sortParam)) {
            const secondarySortArg = {
                type: getRosterComparatorType(sortParam),
                field: SortValues.SECONDARY_SORT_VALUE,
                descend: sortType === SortType.DESC,
            };
            args.push(secondarySortArg);
        }

        return args;
    };

    const getRosterSortValue = (player, sortParam) => {
        switch (sortParam) {
            case SortParams.SINGLES_RATING: {
                try {
                    const singlesRating = getRatingDetails(setRatingChoice({ player, ratingType: 'singles' }), userIsPowered);
                    return parseFloat(singlesRating?.actualValue) || 0;
                } catch (error) {
                    console.warn('Error getting singles rating for sorting:', error);
                    return 0;
                }
            }
            case SortParams.DOUBLES_RATING: {
                try {
                    const doublesRating = getRatingDetails(setRatingChoice({ player, ratingType: 'doubles' }), userIsPowered);
                    return parseFloat(doublesRating?.actualValue) || 0;
                } catch (error) {
                    console.warn('Error getting doubles rating for sorting:', error);
                    return 0;
                }
            }
            case SortParams.PLAYER_COLLEGE_DETAILS: {
                return getGradClassValue(player.playerCollegeDetails?.gradClassName || '');
            }
            case SortParams.PLAYER_HIGH_SCHOOL_DETAILS: {
                if (isRostersRolloutEnabled && player.classYearStatus) {
                    return getGradClassValue(player.classYearStatus || '');
                }
                return getGradClassValue(player.playerHighSchoolDetails?.gradClassName || '');
            }
            default:
                return player[sortParam];
        }
    };

    const getPlayersList = () => {
        const { rosterSortParam, rosterSortType } = rosterTabState;
        const players = schoolDetails?.roster || [];
        const playersToDisplay = players.map((player) => {
            let primarySortValue;
            let secondarySortValue;

            if (rosterSortParam === SortParams.NAME) {
                primarySortValue = getRosterSortValue(player, 'lastName');
                secondarySortValue = getRosterSortValue(player, 'firstName');
            } else if (rosterSortParam === SortParams.SINGLES_RATING) {
                const ratingProgress = player.ratingProgressSingles ?? player.ratingProgress ?? 0;
                if (ratingProgress === 100) {
                    // fully reliable players first
                    primarySortValue = 2;
                } else if (player.utrRange) {
                    // people with UTR range next
                    primarySortValue = 1;
                } else {
                    // unrated players last
                    primarySortValue = 0;
                }
                secondarySortValue = getRosterSortValue(player, rosterSortParam);
            } else if (rosterSortParam === SortParams.DOUBLES_RATING) {
                const ratingProgress = player.ratingProgressDoubles ?? player.ratingProgress ?? 0;
                if (ratingProgress === 100) {
                    // fully reliable players first
                    primarySortValue = 2;
                } else if (player.utrRange) {
                    // people with UTR range next
                    primarySortValue = 1;
                } else {
                    // unrated players last
                    primarySortValue = 0;
                }
                secondarySortValue = getRosterSortValue(player, rosterSortParam);
            } else {
                primarySortValue = getRosterSortValue(player, rosterSortParam);
            }

            return {
                item: player,
                primarySortValue,
                secondarySortValue,
            };
        });

        playersToDisplay.sort(comparator(getComparatorArguments(rosterSortParam, rosterSortType)));
        return playersToDisplay;
    };

    const playersToDisplay = getPlayersList();
    const editYearLabel = isRostersRolloutEnabled ? 'Edit Class Year' : 'Edit Graduation Year';

    const getPlayerRosterActions = (player) => {
        if (!isAdminOrOwner) {
            return [];
        }
        const actions = [
            { label: editYearLabel, value: Actions.UPDATE },
            { label: 'Remove', value: Actions.REMOVE },
        ];

        // Add redshirt options only for college rosters and only for Freshman-Senior (not Graduate/Graduate Student)
        if (isCollege && isRostersRolloutEnabled) {
            // Get the player's class year ID to check if they're Graduate or Graduate Student
            const playerClassYearId = player.classYearStatus ? getClassYearIdFromStatus(player.classYearStatus) : null;
            const isGraduateOrGraduateStudent = playerClassYearId === CLASS_YEAR_IDS.GRADUATE || playerClassYearId === CLASS_YEAR_IDS.GRADUATE_STUDENT;

            // Only show redshirt options for Freshman-Senior (not Graduate/Graduate Student)
            if (!isGraduateOrGraduateStudent && playerClassYearId && playerClassYearId <= CLASS_YEAR_IDS.SENIOR) {
                // Check redshirt status from API response (boolean: true or false)
                const isRedshirt = player.redshirt === true;
                if (isRedshirt) {
                    // Player is redshirted, show option to remove redshirt status
                    actions.splice(1, 0, { label: 'Remove Redshirt Status', value: Actions.REMOVE_REDSHIRT });
                } else {
                    // Player is not redshirted (redshirt is false, undefined, or null), show option to set as redshirt
                    actions.splice(1, 0, { label: 'Set as Redshirt', value: Actions.SET_REDSHIRT });
                }
            }
        }

        return actions;
    };

    return (
        <>
            <RosterHeader
                rosterTabState={rosterTabState}
                clubTierType={clubDetails.tierType}
                isAdminOrOwner={isAdminOrOwner}
                updateRosterTabState={updateRosterTabState}
                user={user}
                showTennisRatings={showTennisRatings}
                showPickleBallRatings={showPickleBallRatings}
            />
            {playersToDisplay.map((player) => (
                <PlayerCard
                    key={`player-${player.item.id}`}
                    data-testid={`club-roster.player-${player.item.id}`}
                    player={player.item}
                    clubProfileViewType={clubDetails.tierType.isCollege ? CLUB_PROFILE_VIEW_TYPES.COLLEGE : CLUB_PROFILE_VIEW_TYPES.HIGHSCHOOL}
                    addMemberLink={
                        isAdminOrOwner && clubDetails.tierType.isHighSchool && !player.item.claimed
                            ? `/${clubType}/${clubDetails.id}/add-members?playerId=${player.item.id}`
                            : null
                    }
                    menuOptions={getPlayerRosterActions(player.item)}
                    menuOnSelected={(selected, e) => {
                        onMenuSelected(e, player.item.id, getPlayerName(player.item.firstName, player.item.lastName), selected);
                    }}
                    showActionRequiredBackground={isAdminOrOwner && clubDetails.tierType.isHighSchool && !player.item.claimed}
                    showDecimals={isAdminOrOwner}
                    showProjectedViewStatus
                    showTennisRatings={showTennisRatings}
                    showPickleBallRatings={showPickleBallRatings}
                    checkGate={checkGate}
                    featureGates={featureGates}
                />
            ))}
        </>
    );
};

Roster.propTypes = {
    clubDetails: PropTypes.objectOf(any).isRequired,
    rosterTabState: PropTypes.objectOf(any).isRequired,
    isAdminOrOwner: PropTypes.bool.isRequired,
    clubType: PropTypes.string.isRequired,
    featureGates: PropTypes.objectOf(any).isRequired,
    user: PropTypes.objectOf(any).isRequired,
    schoolDetails: PropTypes.objectOf(any).isRequired,
    updateRosterTabState: PropTypes.func.isRequired,
    showTennisRatings: PropTypes.bool,
    showPickleBallRatings: PropTypes.bool,
    userIsPowered: PropTypes.bool.isRequired,
    checkGate: PropTypes.func.isRequired,
};

Roster.defaultProps = {
    showTennisRatings: false,
    showPickleBallRatings: false,
};

export default Roster;
